/**
 * Lightweight ownership/permission helpers for org members.
 *
 * Pattern: any member sees all items in the org pool, but mutating
 * actions (edit, delete, reassign) are restricted to the item owner
 * (`uploadedBy`) or to admins/owners of the org.
 *
 * This mirrors the standard ATS pattern (Greenhouse / Lever / Bullhorn /
 * Workable / Ashby).
 */

export type OrgRole = 'owner' | 'admin' | 'member' | null | undefined;

const ADMIN_LIKE: ReadonlyArray<NonNullable<OrgRole>> = ['owner', 'admin'];

/** True when role is owner or admin of the current org. */
export function isOrgAdmin(role: OrgRole): boolean {
  return !!role && ADMIN_LIKE.includes(role);
}

/**
 * True when the current user can edit/delete a record they did or did
 * not create. Falls back to admin override if `uploadedBy` is missing
 * (legacy records).
 */
export function canEditItem(
  uploadedBy: string | null | undefined,
  myUid: string | null | undefined,
  role: OrgRole,
): boolean {
  if (isOrgAdmin(role)) return true;
  if (!myUid || !uploadedBy) return false;
  return uploadedBy === myUid;
}

/** Helper: is this record mine? Used for the My/All toggle and "You" badge. */
export function isMyItem(
  uploadedBy: string | null | undefined,
  myUid: string | null | undefined,
): boolean {
  if (!myUid || !uploadedBy) return false;
  return uploadedBy === myUid;
}

/** Minimal shape we need to look up a member's display name. */
export interface MemberLite {
  userId?: string;
  uid?: string;
  email?: string;
  displayName?: string;
}

/**
 * Resolve a user's display label from an org members list.
 * Falls back gracefully:
 *   1. displayName  → "Jane Doe"
 *   2. email local-part  → "jane"
 *   3. shortened uid     → "u-abc123"
 *   4. "—"               → for unknown / legacy records
 */
export function ownerLabel(
  uploadedBy: string | null | undefined,
  members: ReadonlyArray<MemberLite> | null | undefined,
  myUid?: string | null,
): string {
  if (!uploadedBy) return '—';
  const list = members || [];
  const m = list.find(x => x.userId === uploadedBy || x.uid === uploadedBy);
  const isMe = !!myUid && uploadedBy === myUid;
  if (m?.displayName && m.displayName.trim()) {
    return isMe ? `${m.displayName.trim()} (you)` : m.displayName.trim();
  }
  if (m?.email) {
    const local = m.email.split('@')[0];
    return isMe ? `${local} (you)` : local;
  }
  // Unknown member (e.g. removed user) — show abbreviated id.
  return `u-${uploadedBy.slice(0, 6)}`;
}

